import type { TransactionSplit } from './types';

/**
 * The arithmetic behind splitting a payment between people.
 *
 * Kept away from the components because it's the part that has to be exactly
 * right: shares are whole cents, they must add up to the payment to the penny,
 * and what's left over is your own share.
 */

/** A row in the split editor, before it's been saved. */
export type SplitDraft = {
  /** Local only — React needs a stable key while the row is being typed. */
  key: string;
  name: string;
  /** As typed, so a half-finished "12." doesn't get rewritten under the cursor. */
  amount: string;
};

let nextKey = 0;

export function emptyDraft(name = ''): SplitDraft {
  nextKey += 1;
  return { key: `s${nextKey}`, name, amount: '' };
}

/** Cents from a typed amount. NaN when it isn't a number at all. */
export function parseAmountCents(input: string): number {
  const cleaned = input.replace(/[^0-9.]/g, '');
  if (!cleaned) return NaN;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100);
}

export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Divides `totalCents` into `parts` whole-cent shares.
 *
 * The remainder goes to the earliest shares — £10 three ways is 3.34 / 3.33 /
 * 3.33, never 3.33 each with a penny quietly lost.
 */
export function evenShares(totalCents: number, parts: number): number[] {
  if (parts <= 0) return [];
  const base = Math.floor(totalCents / parts);
  const remainder = totalCents - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < remainder ? 1 : 0));
}

/**
 * What each row actually owes, blanks included.
 *
 * A row left empty takes an equal share of whatever the filled ones leave —
 * and so does the payer, which is why the remainder divides into one part
 * more than there are blanks. Typing two figures and leaving the third blank
 * is a normal way to split a bill, and it means the same thing here as it
 * does in a quick-add sentence ("split with sam 50 and alex").
 */
export function priceDrafts(
  rows: SplitDraft[],
  totalCents: number,
  existing: TransactionSplit[] = [],
): number[] {
  const typed = rows.map((row) => {
    const cents = parseAmountCents(row.amount);
    return Number.isNaN(cents) ? null : cents;
  });

  const namedTotal = typed.reduce((total: number, cents) => total + (cents ?? 0), 0);
  const blanks = typed.filter((cents) => cents === null).length;
  const left = Math.max(0, totalCents - claimedCents(existing) - namedTotal);
  const even = evenShares(left, blanks + 1);

  let next = 0;
  return typed.map((cents) => cents ?? even[next++] ?? 0);
}

export function draftTotalCents(rows: SplitDraft[]): number {
  return rows.reduce((total, row) => {
    const cents = parseAmountCents(row.amount);
    return total + (Number.isNaN(cents) ? 0 : cents);
  }, 0);
}

export function openOwedCents(splits: TransactionSplit[]): number {
  return splits.filter((s) => !s.settledAt).reduce((total, s) => total + s.amountCents, 0);
}

export function claimedCents(splits: TransactionSplit[]): number {
  return splits.reduce((total, s) => total + s.amountCents, 0);
}

/**
 * Why these rows can't be saved, or null when they can. Mirrors the checks the
 * API makes, so the common mistakes are caught before a round trip.
 */
export function splitProblem(
  rows: SplitDraft[],
  totalCents: number,
  existing: TransactionSplit[] = [],
): string | null {
  if (rows.length === 0) return 'Add at least one person.';

  const names = rows.map((r) => r.name.trim().toLowerCase());
  if (names.some((n) => !n)) return 'Every share needs a name.';

  const taken = new Set([...names, ...existing.map((s) => s.person.toLowerCase())]);
  if (taken.size !== names.length + existing.length) {
    return 'Two shares can’t be for the same person.';
  }

  for (const row of rows) {
    // Blank is allowed — it means an equal share. Something typed has to be a
    // real figure, though; a typo shouldn't quietly become "share it out".
    if (!row.amount.trim()) continue;
    const cents = parseAmountCents(row.amount);
    if (Number.isNaN(cents) || cents <= 0) return `What does ${row.name.trim()} owe?`;
  }

  const priced = priceDrafts(rows, totalCents, existing);
  if (priced.some((cents) => cents <= 0)) {
    return 'There’s nothing left of the payment to share out.';
  }
  if (claimedCents(existing) + priced.reduce((a, b) => a + b, 0) > totalCents) {
    return 'Those shares add up to more than the payment.';
  }
  return null;
}

/** Wire shape for the API, with blanks priced into real figures. */
export function toShares(
  rows: SplitDraft[],
  totalCents: number,
  existing: TransactionSplit[] = [],
): Array<{ name: string; amount: number }> {
  const priced = priceDrafts(rows, totalCents, existing);
  return rows.map((row, i) => ({ name: row.name.trim(), amount: (priced[i] ?? 0) / 100 }));
}
